import { RefObject, useCallback, useState } from 'react';

import { useUserStatus } from '~/hooks/userStatus';
import { fetchJson } from '~/lib/fetch';
import { QuestionOrReviewSchema } from '~/types/question-review';

interface ReplyModalProps {
  comment?: any;
  type: 'dealcomment' | 'dealreview';
  dealId: number;
  mutateComment?: Function;
  mutateReviewStats?: Function;
  successCallback?: Function;
  formRef?: RefObject<HTMLFormElement>;
}

export function useSubmitReply({
  type,
  comment,
  dealId,
  mutateComment = () => {},
  mutateReviewStats = () => {},
  successCallback,
  formRef,
}: ReplyModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { mutateUserStatus } = useUserStatus(dealId);
  const [errors, setErrors] = useState({});

  const handleSubmit = useCallback(
    (values: QuestionOrReviewSchema) => {
      const subPath = type === 'dealcomment' ? 'questions' : 'reviews';
      let url;

      const post: {
        comment: string;
        followup: boolean;
        title?: string;
        rating?: number;
      } = {
        comment: values.comment,
        followup: !!values.notify,
      };

      if (!comment) {
        url = `/api/v2/deals/${dealId}/${subPath}/`;
        if (type === 'dealreview') {
          post.title = values.title;
          post.rating = values.rating;
        }
      } else {
        url = `/api/v2/deals/${dealId}/${subPath}/${comment?.id}/reply/`;
      }

      const urlEncodedPost = Object.entries(post)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

      setIsLoading(true);
      setErrors({});

      fetchJson(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedPost,
      })
        .then(async (data) => {
          await Promise.all([
            mutateComment(),
            mutateReviewStats(),
            mutateUserStatus(),
          ]);

          if (successCallback) {
            successCallback(data);
          }

          setIsLoading(false);
        })
        .catch((err) => {
          err.response
            ?.json()
            .then((data: { [key: string]: Array<string> | String }) => {
              setErrors(data);

              for (const [key, value] of Object.entries(data)) {
                formRef?.current?.setError(key, {
                  message:
                    typeof value === 'string'
                      ? value
                      : value?.[0] || 'Something went wrong',
                });
              }
            });

          setIsLoading(false);
        });
    },
    [
      type,
      comment,
      dealId,
      mutateComment,
      mutateReviewStats,
      mutateUserStatus,
      successCallback,
      formRef,
    ],
  );

  return { handleSubmit, isLoading, errors };
}
